import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { usePortfolio } from '../../context/PortfolioContext';
import toast from 'react-hot-toast';
// @ts-ignore
import Spinner from '../../components/Spinner';
import { PlusCircle, RefreshCw, List as ListIcon, Trash2, Upload, ArrowUpDown } from 'lucide-react';
import { fetchTransactions, deleteTransaction, postManualTrade, uploadTransactions } from '../../services/api';
// @ts-ignore
import { parseCsv } from '../../utils/csvParser';

// --- Add Trade Form Component ---
interface AddTradeFormProps {
  portfolioId: string;
  onClose: () => void;
  onSuccess: () => void;
}

const AddTradeForm: React.FC<AddTradeFormProps> = ({ portfolioId, onClose, onSuccess }) => {
  const { currentUser } = useAuth();
  const [ticker, setTicker] = useState('');
  const [action, setAction] = useState('BUY');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const tradeAmount = parseFloat(amount);
    const tradePrice = parseFloat(price);
    
    if (!ticker.trim() || !action || isNaN(tradeAmount) || isNaN(tradePrice) || tradeAmount <= 0 || tradePrice <= 0) {
      toast.error('Please fill in all fields with valid numbers.');
      return;
    }
    
    if (!currentUser) return;
    
    setIsSubmitting(true);
    const loadingToast = toast.loading('Adding transaction...');
    
    try {
      const token = await currentUser.getIdToken();
      const trade = {
        ticker: ticker.toUpperCase().trim(),
        action,
        amount: tradeAmount,
        price: tradePrice,
      };
      await postManualTrade(token, portfolioId, trade);
      toast.success('Transaction added!', { id: loadingToast });
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(`Failed to add transaction: ${error.message}`, { id: loadingToast });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-xl shadow-2xl border border-slate-200 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
          <h3 className="text-lg font-bold text-slate-900">Add Transaction</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <span className="text-2xl">&times;</span>
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Ticker Symbol</label>
            <input
              type="text"
              value={ticker}
              onChange={(e) => setTicker(e.target.value)}
              placeholder="e.g., AAPL"
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isSubmitting}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Action</label>
            <select
              value={action}
              onChange={(e) => setAction(e.target.value)}
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isSubmitting}
            >
              <option value="BUY">Buy</option>
              <option value="SELL">Sell</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Shares</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Number of shares"
              step="0.01"
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isSubmitting}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Price per Share</label>
            <input
              type="number"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="Price"
              step="0.01"
              className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isSubmitting}
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding...' : 'Add Transaction'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface Transaction {
  id: string;
  ticker: string;
  action: string;
  amount: number;
  price: number;
  timestamp?: string;
}

type SortDirection = 'asc' | 'desc';
type SortKey = 'timestamp' | 'ticker' | 'action' | 'amount' | 'price' | 'total';

interface SortConfig {
  key: SortKey;
  direction: SortDirection;
}

const TransactionsView: React.FC = () => {
  const { currentUser } = useAuth();
  const { activePortfolioId, activePortfolio, refreshPortfolios } = usePortfolio();
  
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'timestamp', direction: 'desc' });

  const loadTransactions = useCallback(async () => {
    if (!currentUser || !activePortfolioId) {
      setTransactions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const token = await currentUser.getIdToken();
      const data = await fetchTransactions(token, activePortfolioId);
      setTransactions(data.transactions || []);
    } catch (error: any) {
      console.error('Failed to load transactions:', error);
      toast.error(`Could not load transactions: ${error.message}`);
      setTransactions([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, activePortfolioId]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  // Sort transactions
  const sortedTransactions = useMemo(() => {
    if (!transactions || transactions.length === 0) return [];
    
    const sortableItems = [...transactions];
    
    sortableItems.sort((a: any, b: any) => {
      let aValue: any;
      let bValue: any;
      
      // Handle special cases for sorting
      if (sortConfig.key === 'timestamp') {
        aValue = new Date(a.date || a.timestamp).getTime();
        bValue = new Date(b.date || b.timestamp).getTime();
      } else if (sortConfig.key === 'total') {
        aValue = a.amount * a.price;
        bValue = b.amount * b.price;
      } else {
        aValue = a[sortConfig.key];
        bValue = b[sortConfig.key];
      }
      
      // Handle null/undefined values
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      // Compare values
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      const strA = String(aValue).toLowerCase();
      const strB = String(bValue).toLowerCase();
      if (strA < strB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (strA > strB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sortableItems;
  }, [transactions, sortConfig]);

  const handleSort = (key: SortKey) => {
    let direction: SortDirection = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const renderSortIcon = (columnKey: SortKey) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown size={12} className="inline-block ml-1 opacity-30" />;
    }
    return <span className="text-blue-600 ml-1">{sortConfig.direction === 'asc' ? '▲' : '▼'}</span>;
  };

  const handleAddSuccess = () => {
    loadTransactions();
    if (refreshPortfolios) refreshPortfolios();
  };

  const handleDelete = async (txId: string) => {
    if (!currentUser || !activePortfolioId) return;
    if (!window.confirm('Delete this transaction? P&L will be recalculated.')) return;

    const loadingToast = toast.loading('Deleting...');
    try {
      const token = await currentUser.getIdToken();
      await deleteTransaction(token, activePortfolioId, txId);
      toast.success('Transaction deleted!', { id: loadingToast });
      loadTransactions();
      if (refreshPortfolios) refreshPortfolios();
    } catch (error: any) {
      toast.error(`Failed to delete: ${error.message}`, { id: loadingToast });
    }
  };

  const handleClearAll = async () => {
    if (!currentUser || !activePortfolioId || transactions.length === 0) return;
    
    const confirmed = window.confirm(
      `⚠️ WARNING: This will delete ALL ${transactions.length} transactions from this portfolio!\n\n` +
      'This action CANNOT be undone.\n\n' +
      'Are you absolutely sure you want to continue?'
    );
    if (!confirmed) return;

    // Second confirmation for safety
    const doubleConfirmed = window.confirm(
      '🚨 FINAL CONFIRMATION\n\n' +
      'This will permanently delete all transaction history.\n' +
      'Click OK to proceed.'
    );
    if (!doubleConfirmed) return;

    const loadingToast = toast.loading(`Deleting ${transactions.length} transactions...`);
    try {
      const token = await currentUser.getIdToken();
      
      // Delete each transaction
      for (const tx of transactions) {
        await deleteTransaction(token, activePortfolioId, tx.id);
      }
      
      toast.success(`Successfully deleted ${transactions.length} transactions`, { id: loadingToast });
      loadTransactions();
      if (refreshPortfolios) refreshPortfolios();
    } catch (error: any) {
      toast.error(`Failed to clear transactions: ${error.message}`, { id: loadingToast });
    }
  };

  const formatDate = (timestamp: string) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleUploadClick = () => {
    if (!activePortfolioId) {
      toast.error('Please select a portfolio first.');
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentUser || !activePortfolioId) return;

    setIsUploading(true);
    const loadingToast = toast.loading('Processing CSV file...');

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const csvText = e.target?.result as string;
        const jsonData = await parseCsv(csvText);
        
        if (jsonData.length === 0) {
          throw new Error('CSV is empty or invalid.');
        }
        
        const token = await currentUser.getIdToken();
        const response = await uploadTransactions(token, activePortfolioId, jsonData);
        
        toast.success(response.message || 'Upload complete!', { id: loadingToast });
        loadTransactions();
        if (refreshPortfolios) refreshPortfolios();
      } catch (error: any) {
        toast.error(`Upload failed: ${error.message}`, { id: loadingToast });
      } finally {
        setIsUploading(false);
        if (event.target) event.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner />
      </div>
    );
  }

  if (!activePortfolioId) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-8">
        <ListIcon size={64} className="text-slate-300 mb-4" />
        <h2 className="text-2xl font-bold text-slate-800 mb-2">No Portfolio Selected</h2>
        <p className="text-slate-600">Select a portfolio to view transactions</p>
      </div>
    );
  }

  // ── Summary stats ──
  const totalInvested = sortedTransactions
    .filter((t: any) => (t.action || '').toUpperCase() === 'BUY')
    .reduce((sum: number, t: any) => sum + (t.amount * t.price || 0), 0);
  const totalProceeds = sortedTransactions
    .filter((t: any) => (t.action || '').toUpperCase() === 'SELL')
    .reduce((sum: number, t: any) => sum + (t.amount * t.price || 0), 0);
  const buyCount = sortedTransactions.filter((t: any) => (t.action || '').toUpperCase() === 'BUY').length;
  const sellCount = sortedTransactions.filter((t: any) => (t.action || '').toUpperCase() === 'SELL').length;
  const fmtMoney = (n: number) => n >= 1_000_000 ? `$${(n/1_000_000).toFixed(2)}M` : n >= 1_000 ? `$${(n/1_000).toFixed(1)}K` : `$${n.toFixed(2)}`;

  return (
    <div className="h-full overflow-y-auto bg-[#F0F2F5]">
      {/* ── Summary strip ── */}
      {transactions.length > 0 && (
        <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-6 overflow-x-auto">
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-slate-400 uppercase tracking-wider font-bold">Portfolio</span>
            <span className="text-sm font-semibold text-slate-800">{activePortfolio?.name || '—'}</span>
          </div>
          <div className="w-px h-5 bg-slate-200 shrink-0" />
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-xs text-slate-500">{buyCount} buys</span>
            <span className="text-xs font-semibold text-slate-800 font-mono">{fmtMoney(totalInvested)}</span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="w-2 h-2 rounded-full bg-red-400" />
            <span className="text-xs text-slate-500">{sellCount} sells</span>
            <span className="text-xs font-semibold text-slate-800 font-mono">{fmtMoney(totalProceeds)}</span>
          </div>
          <div className="w-px h-5 bg-slate-200 shrink-0" />
          <div className="text-xs text-slate-500 shrink-0">
            Net deployed: <span className="font-semibold text-slate-800 font-mono">{fmtMoney(totalInvested - totalProceeds)}</span>
          </div>
        </div>
      )}

      <div className="p-6 md:p-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-200 pb-6 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
            <ListIcon className="text-blue-600" size={26} />
            Order Blotter
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            {activePortfolio?.name || 'Portfolio'} · {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button 
            onClick={loadTransactions}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 hover:text-blue-600 hover:border-blue-300 transition-all"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          
          <button 
            onClick={handleUploadClick}
            disabled={isUploading}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all ${
              isUploading
                ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm'
            }`}
          >
            <Upload size={16} />
            {isUploading ? 'Uploading...' : 'Upload CSV'}
          </button>
          
          <button 
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-blue-600 text-white hover:bg-blue-700 shadow-sm transition-all"
          >
            <PlusCircle size={16} />
            Add Transaction
          </button>

          {transactions.length > 0 && (
            <button 
              onClick={handleClearAll}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold bg-red-600 text-white hover:bg-red-700 shadow-sm transition-all"
            >
              <Trash2 size={16} />
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Transaction List */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden mb-8">
        {transactions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('timestamp')}
                  >
                    Date {renderSortIcon('timestamp')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('ticker')}
                  >
                    Ticker {renderSortIcon('ticker')}
                  </th>
                  <th 
                    className="px-6 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('action')}
                  >
                    Action {renderSortIcon('action')}
                  </th>
                  <th 
                    className="px-6 py-3 text-right text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('amount')}
                  >
                    Shares {renderSortIcon('amount')}
                  </th>
                  <th 
                    className="px-6 py-3 text-right text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('price')}
                  >
                    Price {renderSortIcon('price')}
                  </th>
                  <th 
                    className="px-6 py-3 text-right text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                    onClick={() => handleSort('total')}
                  >
                    Total {renderSortIcon('total')}
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {sortedTransactions.map((tx: any) => (
                  <tr key={tx.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4 text-sm text-slate-700">
                      {formatDate(tx.date || tx.timestamp)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-slate-900">{tx.ticker}</div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${
                        tx.action === 'BUY' 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {tx.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-slate-900 font-medium">
                      {tx.amount}
                    </td>
                    <td className="px-6 py-4 text-right text-slate-900">
                      ${tx.price?.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-right text-slate-900 font-semibold">
                      ${(tx.amount * tx.price).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => handleDelete(tx.id)}
                        className="inline-flex items-center justify-center p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        title="Delete transaction"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center text-slate-500">
            <ListIcon size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-lg font-medium text-slate-700 mb-2">No transactions yet</p>
            <p className="text-sm">Add your first transaction to start tracking your portfolio</p>
          </div>
        )}
      </div>

      {/* Add Trade Modal */}
      {showAddForm && (
        <AddTradeForm
          portfolioId={activePortfolioId}
          onClose={() => setShowAddForm(false)}
          onSuccess={handleAddSuccess}
        />
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileChange}
        className="hidden"
      />
      </div> {/* end inner padding div */}
    </div>
  );
};

export default TransactionsView;
